import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/layout/Header.jsx';
import Footer from './components/layout/Footer.jsx';
import SkyBackdrop from './components/layout/SkyBackdrop.jsx';
import CookieConsent from './components/ui/CookieConsent.jsx';

const Home = lazy(() => import('./pages/Home.jsx'));
const QrCodeGenerator = lazy(() => import('./pages/converters/QrCodeGenerator.jsx'));
const PdfToWord = lazy(() => import('./pages/converters/PdfToWord.jsx'));
const ImageCompressor = lazy(() => import('./pages/converters/ImageCompressor.jsx'));
const ImageToPdf = lazy(() => import('./pages/converters/ImageToPdf.jsx'));
const MergePdf = lazy(() => import('./pages/converters/MergePdf.jsx'));
const ImageToText = lazy(() => import('./pages/converters/ImageToText.jsx'));
const PdfToJpg = lazy(() => import('./pages/converters/PdfToJpg.jsx'));
const WordToPdf = lazy(() => import('./pages/converters/WordToPdf.jsx'));
const CsvExcelConverter = lazy(() => import('./pages/converters/CsvExcelConverter.jsx'));
const QrCodeReader = lazy(() => import('./pages/converters/QrCodeReader.jsx'));

const UniversalConverter = lazy(() => import('./pages/converters/UniversalConverter.jsx'));
const SplitPdf = lazy(() => import('./pages/converters/SplitPdf.jsx'));
const Privacy = lazy(() => import('./pages/legal/Privacy.jsx'));
const Terms = lazy(() => import('./pages/legal/Terms.jsx'));
const About = lazy(() => import('./pages/legal/About.jsx'));
const Contact = lazy(() => import('./pages/legal/Contact.jsx'));

function PageSkeleton() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-sky-50/50 dark:bg-slate-950">
      <div className="flex flex-col items-center gap-4 text-slate-400">
        <div className="w-10 h-10 border-2 border-sky-200 border-t-brand-500 rounded-full animate-spin" />
        <p className="text-sm font-mono">Loading tool...</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();

  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', 'G-NVTWPKQ9CP', {
        page_path: location.pathname + location.search
      });
    }

    // Performance Optimization: Defer AdSense loading
    const loadAds = setTimeout(() => {
      const script = document.createElement('script');
      script.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-2677960471330646";
      script.async = true;
      script.crossOrigin = "anonymous";
      document.head.appendChild(script);
    }, 3000); // 3-second delay to prioritize core metrics

    return () => clearTimeout(loadAds);
  }, [location]);

  return (
    <div className="relative z-10 flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">
        <Suspense fallback={<PageSkeleton />}>
          <div key={location.pathname} className="page-enter">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/universal-converter" element={<UniversalConverter />} />
              <Route path="/qr-code-generator" element={<QrCodeGenerator />} />
              <Route path="/pdf-to-word" element={<PdfToWord />} />
              <Route path="/image-compressor" element={<ImageCompressor />} />
              <Route path="/image-to-pdf" element={<ImageToPdf />} />
              <Route path="/merge-pdf" element={<MergePdf />} />
              <Route path="/image-to-text" element={<ImageToText />} />
              <Route path="/pdf-to-jpg" element={<PdfToJpg />} />
              <Route path="/word-to-pdf" element={<WordToPdf />} />
              <Route path="/csv-excel-converter" element={<CsvExcelConverter />} />
              <Route path="/qr-code-reader" element={<QrCodeReader />} />
              <Route path="/youtube-downloader" element={<UniversalConverter />} />
              <Route path="/instagram-downloader" element={<UniversalConverter />} />

              <Route path="/split-pdf" element={<SplitPdf />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
            </Routes>
          </div>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <div className="relative min-h-screen bg-slate-50 dark:bg-slate-950">
        <AppRoutes />
        <CookieConsent />
      </div>
    </BrowserRouter>
  );
}
